/**
 * Step 8 read-only presentation of one competency's capture record
 * (HG-3B G3-04.1).
 *
 * The mirror of CompetencyCapturePanel: the same information, in the same
 * reading order, with nothing editable. Where Step 6 offers a selector, this
 * states what was selected; where Step 6 shows the weakest-dimension rule beside
 * a live proposal, this shows the rule beside the proposal that was recorded.
 *
 * Everything displayed here comes from presentCapture in @orca360/shared, which
 * the pilot PDF and DOCX generators also call. That is deliberate: the report on
 * screen and the report on paper must not be able to word the same record
 * differently.
 *
 * The rules this component exists to keep:
 *   - GCH-35 and ICD 9.3: a value that was never recorded reads "Not recorded".
 *     Never zero, never a default, never inferred. A historical row therefore
 *     shows a full set of "Not recorded" cells and a banner saying why.
 *   - ICD 8.7: the limiting factor is displayed as stored and is never
 *     recomputed here, not even when the dimensions would imply a different one.
 *   - GCH-13 and DEC-28: the override record shows proposed, final, category,
 *     actor and timestamp; the note appears only for the `other` category,
 *     because on every other category there is none.
 */

import {
  NOT_RECORDED_LABEL,
  WEAKEST_DIMENSION_RULE_STATEMENT,
  presentCapture,
  type CaptureRecord,
  type ResolvedFramework,
} from '@orca360/shared';

export interface CompetencyCaptureSummaryProps {
  capture: CaptureRecord | null | undefined;
  /** The instructor's recorded grade, for the override comparison. */
  recordedGrade: number | null;
  framework: ResolvedFramework | null;
}

/** Timestamps are audit facts; render them, do not reformat them into a guess. */
function formatTimestamp(value: string | null): string {
  if (!value) return NOT_RECORDED_LABEL;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function CompetencyCaptureSummary({
  capture,
  recordedGrade,
  framework,
}: CompetencyCaptureSummaryProps) {
  const presented = presentCapture(capture, recordedGrade, framework);

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="capture-summary"
    >
      <header className="border-b border-slate-200 px-3 py-2">
        <h5 className="text-xs font-semibold text-navy">Assessment dimensions</h5>
      </header>

      <div className="space-y-3 px-3 py-3">
        {presented.historical && !presented.dimensionsRecorded && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold">Dimensions {NOT_RECORDED_LABEL.toLowerCase()}</p>
            <p className="mt-1">
              This assessment was recorded before the assessment dimensions existed. Its grade
              stands as issued; the dimensions were never captured and are not inferred.
            </p>
          </div>
        )}

        {/* The three dimensions, in capture order, with the word picture that
            applied under the framework version this grade was recorded against. */}
        <dl className="grid gap-3 sm:grid-cols-3">
          {presented.dimensions.map((d) => (
            <div key={d.key}>
              <dt className="text-[11px] font-semibold text-navy">{d.label}</dt>
              <dd
                className={`mt-0.5 text-xs ${d.code ? 'text-slate-700' : 'italic text-slate-400'}`}
              >
                {d.position != null ? `${d.position}. ` : ''}
                {d.text}
              </dd>
            </div>
          ))}
        </dl>

        <dl className="grid gap-3 sm:grid-cols-3">
          <div>
            <dt className="text-[11px] font-semibold text-navy">Limiting factor</dt>
            <dd
              className={`mt-0.5 text-xs ${
                presented.limitingFactor === NOT_RECORDED_LABEL
                  ? 'italic text-slate-400'
                  : 'text-slate-700'
              }`}
            >
              {presented.limitingFactor}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-navy">Proposed grade</dt>
            <dd
              className={`mt-0.5 text-xs ${
                presented.proposedGrade === NOT_RECORDED_LABEL
                  ? 'italic text-slate-400'
                  : 'text-slate-700'
              }`}
            >
              {presented.proposedGrade}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-navy">Recorded grade</dt>
            <dd className="mt-0.5 text-xs text-slate-700">
              {recordedGrade ?? NOT_RECORDED_LABEL}
            </dd>
          </div>
        </dl>

        {/* GCH-13: wherever a proposal appears, the rule appears with it. */}
        <p className="text-[11px] text-slate-500">{WEAKEST_DIMENSION_RULE_STATEMENT}</p>

        {/* The override record, only where one occurred (GCH-13, DEC-28). */}
        {presented.override && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold text-amber-900">Grade override recorded</p>
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <dt className="text-[11px] font-semibold text-amber-900">Proposed</dt>
                <dd className="text-xs text-amber-900">
                  {presented.override.proposedGrade ?? NOT_RECORDED_LABEL}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-amber-900">Final</dt>
                <dd className="text-xs text-amber-900">
                  {presented.override.recordedGrade ?? NOT_RECORDED_LABEL}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-amber-900">Category</dt>
                <dd className="text-xs text-amber-900">{presented.override.categoryLabel}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-amber-900">Recorded by</dt>
                <dd className="text-xs text-amber-900">
                  {presented.override.actor ?? NOT_RECORDED_LABEL}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-amber-900">Recorded at</dt>
                <dd className="text-xs text-amber-900">
                  {formatTimestamp(presented.override.at)}
                </dd>
              </div>
            </dl>
            {/* DEC-28: a note exists for `other` alone. */}
            {presented.override.note && (
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-amber-900">Explanation</p>
                <p className="text-xs text-amber-900">{presented.override.note}</p>
              </div>
            )}
          </div>
        )}

        {/* G3-07 / ICD 3.1.4. The ICD is silent on presentation, so this follows
            the dimensions' styling above. */}
        <dl className="grid gap-3 sm:grid-cols-2 border-t border-slate-100 pt-3">
          <div>
            <dt className="text-[11px] font-semibold text-navy">Development level</dt>
            <dd
              className={`mt-0.5 text-xs ${
                presented.developmentLevel === NOT_RECORDED_LABEL
                  ? 'italic text-slate-400'
                  : 'text-slate-700'
              }`}
            >
              {presented.developmentLevel}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-navy">Level source</dt>
            <dd
              className={`mt-0.5 text-xs ${
                presented.developmentLevelSource === NOT_RECORDED_LABEL
                  ? 'italic text-slate-400'
                  : 'text-slate-700'
              }`}
            >
              {presented.developmentLevelSource}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

export default CompetencyCaptureSummary;
